import React, { useEffect, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tooltip,
  CircularProgress
} from "@mui/material";

import { getProjects } from "../../services/projectService";

function CapacityCalendar({ employeeId, month = null, year = null, refreshKey = null }) {
  const [loading, setLoading] = useState(false);
  const [capacityMap, setCapacityMap] = useState({});

  const today = new Date();
  const currentMonth = month ?? today.getMonth();
  const currentYear = year ?? today.getFullYear();

  // Returns "YYYY-MM-DD" in LOCAL time (toISOString is UTC and shifts dates in UTC+ zones).
  const localStr = (date) => {
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Returns "YYYY-MM-DD" of the Monday of the week containing the given date.
  const getWeekStartKey = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
    return localStr(d);
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        if (employeeId) {
          const projectsData = await getProjects("", employeeId, true);

          // Build capacity map for each date using per-employee assignment dates
          const capacityByDate = {};

          projectsData.forEach((project) => {
            // Use per-employee assignment window (preserves historical removals)
            const rangeStart = project.assignment_start_date || project.start_date;
            const rangeEnd = project.assignment_end_date || project.end_date;

            if (!rangeStart || !rangeEnd) return;

            const startDate = new Date(rangeStart);
            const endDate = new Date(rangeEnd);
            const weeklyOverrides = project.weekly_capacity || {};

            let currentDate = new Date(startDate);

            while (currentDate <= endDate) {
              const dateKey = localStr(currentDate);
              const weekKey = getWeekStartKey(currentDate);
              // Use week-specific override if set, otherwise fall back to global capacity
              const cap = weeklyOverrides[weekKey] ?? (project.capacity_percent || 0);

              if (!capacityByDate[dateKey]) {
                capacityByDate[dateKey] = {
                  total: 0,
                  projects: []
                };
              }

              capacityByDate[dateKey].total += cap;
              capacityByDate[dateKey].projects.push({
                name: project.project_name,
                capacity: cap
              });

              currentDate.setDate(currentDate.getDate() + 1);
            }
          });

          setCapacityMap(capacityByDate);
        }
      } catch (error) {
        console.error("Error loading calendar data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [employeeId, refreshKey]);

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getCapacityColor = (capacity) => {
    if (capacity >= 100) return "#ef4444";
    if (capacity >= 75) return "#f97316";
    if (capacity >= 50) return "#eab308";
    if (capacity >= 25) return "#84cc16";
    return "#e5e7eb";
  };

  const getTextColor = (capacity) => {
    if (capacity >= 50) return "#fff";
    return "#334155";
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);

  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Card sx={{ borderRadius: 2, bgcolor: "background.default" }}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} mb={2}>
          {monthNames[currentMonth]} {currentYear}
        </Typography>

        <Box sx={{ overflowX: "auto" }}>
          <Grid container spacing={0.5} sx={{ minWidth: 400 }}>
            {dayNames.map((day) => (
              <Grid item xs={12 / 7} key={day}>
                <Box
                  sx={{
                    p: 1,
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: 11,
                    color: "text.secondary",
                    background: "#e2e8f0",
                    borderRadius: 1
                  }}
                >
                  {day}
                </Box>
              </Grid>
            ))}

            {days.map((day, index) => {
              if (!day) {
                return (
                  <Grid item xs={12 / 7} key={`empty-${index}`}>
                    <Box sx={{ p: 1 }} />
                  </Grid>
                );
              }

              const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const dayCapacity = capacityMap[dateKey];
              const capacity = dayCapacity?.total || 0;
              const isFullyBooked = capacity >= 100;
              const bgColor = getCapacityColor(capacity);

              return (
                <Grid item xs={12 / 7} key={day}>
                  <Tooltip
                    title={
                      dayCapacity ? (
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {dateKey}: {capacity}% allocated
                          </Typography>
                          {dayCapacity.projects.map((proj, i) => (
                            <Typography key={i} variant="caption" display="block">
                              • {proj.name}: {proj.capacity}%
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        "No capacity allocated"
                      )
                    }
                    arrow
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        textAlign: "center",
                        borderRadius: 1.5,
                        background: bgColor,
                        color: getTextColor(capacity),
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: "pointer",
                        border: isFullyBooked ? "2px solid #991b1b" : "1px solid #cbd5e1",
                        transition: "all 0.2s",
                        boxShadow: isFullyBooked ? "0 0 8px rgba(239, 68, 68, 0.5)" : "none",
                        "&:hover": {
                          transform: "scale(1.05)",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                        }
                      }}
                    >
                      <Box>{day}</Box>
                      {capacity > 0 && (
                        <Box sx={{ fontSize: 10, mt: 0.3, opacity: 0.8 }}>
                          {capacity}%
                        </Box>
                      )}
                      {dayCapacity && (
                        <Box
                          sx={{
                            fontSize: 9,
                            mt: 0.2,
                            opacity: 0.85,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {dayCapacity.projects
                            .slice(0, 2)
                            .map((proj) => proj.name?.slice(0, 3).toUpperCase())
                            .join(", ")}
                          {dayCapacity.projects.length > 2 && ` +${dayCapacity.projects.length - 2}`}
                        </Box>
                      )}
                      {isFullyBooked && (
                        <Box sx={{ fontSize: 11, mt: 0.2, fontWeight: 700 }}>
                          ⚠
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box display="flex" gap={2} mt={3} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: 1,
                background: "#ef4444",
                border: "2px solid #991b1b"
              }}
            />
            <Typography variant="caption">100% (Fully Booked)</Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: 1,
                background: "#f97316"
              }}
            />
            <Typography variant="caption">75-99%</Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: 1,
                background: "#eab308"
              }}
            />
            <Typography variant="caption">50-74%</Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: 1,
                background: "#84cc16"
              }}
            />
            <Typography variant="caption">25-49%</Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: 1,
                background: "#e5e7eb"
              }}
            />
            <Typography variant="caption">0-24%</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default CapacityCalendar;
